import { useState, useEffect, useCallback, useRef } from 'react';
import {
  NotificationChannel,
  CreateNotificationChannelData,
  notificationChannelService,
} from '../../../../api/cansense/notificationChannelApi';

interface UseNotificationChannelsReturn {
  channels: NotificationChannel[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  createChannel: (data: CreateNotificationChannelData) => Promise<NotificationChannel>;
  updateChannel: (
    id: number,
    data: Partial<CreateNotificationChannelData>
  ) => Promise<NotificationChannel>;
  deleteChannel: (id: number) => Promise<void>;
  testChannel: (id: number) => Promise<{ success: boolean; message: string }>;
  toggleChannel: (id: number, enabled: boolean) => Promise<NotificationChannel>;
}

export const useNotificationChannels = (): UseNotificationChannelsReturn => {
  const [channels, setChannels] = useState<NotificationChannel[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const mountedRef = useRef(true);

  const fetchChannels = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await notificationChannelService.getChannels();
      if (mountedRef.current) {
        setChannels(data);
      }
    } catch (err) {
      if (mountedRef.current) {
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to fetch notification channels';
        setError(errorMessage);
        console.error('Failed to fetch notification channels:', err);
      }
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    fetchChannels();

    return () => {
      mountedRef.current = false;
    };
  }, [fetchChannels]);

  const createChannel = useCallback(
    async (data: CreateNotificationChannelData): Promise<NotificationChannel> => {
      try {
        const newChannel = await notificationChannelService.createChannel(data);
        if (mountedRef.current) {
          setChannels((prev) => [...prev, newChannel]);
        }
        return newChannel;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to create notification channel';
        throw new Error(errorMessage);
      }
    },
    []
  );

  const updateChannel = useCallback(
    async (
      id: number,
      data: Partial<CreateNotificationChannelData>
    ): Promise<NotificationChannel> => {
      try {
        const updatedChannel = await notificationChannelService.updateChannel(id, data);
        if (mountedRef.current) {
          setChannels((prev) =>
            prev.map((c) => (c.id === id ? updatedChannel : c))
          );
        }
        return updatedChannel;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to update notification channel';
        throw new Error(errorMessage);
      }
    },
    []
  );

  const deleteChannel = useCallback(async (id: number): Promise<void> => {
    try {
      await notificationChannelService.deleteChannel(id);
      if (mountedRef.current) {
        setChannels((prev) => prev.filter((c) => c.id !== id));
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to delete notification channel';
      throw new Error(errorMessage);
    }
  }, []);

  const testChannel = useCallback(
    async (id: number): Promise<{ success: boolean; message: string }> => {
      try {
        return await notificationChannelService.testChannel(id);
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to test notification channel';
        throw new Error(errorMessage);
      }
    },
    []
  );

  const toggleChannel = useCallback(
    async (id: number, enabled: boolean): Promise<NotificationChannel> => {
      try {
        const updatedChannel = await notificationChannelService.toggleChannel(id, enabled);
        if (mountedRef.current) {
          setChannels((prev) =>
            prev.map((c) => (c.id === id ? updatedChannel : c))
          );
        }
        return updatedChannel;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to toggle notification channel';
        throw new Error(errorMessage);
      }
    },
    []
  );

  return {
    channels,
    loading,
    error,
    refetch: fetchChannels,
    createChannel,
    updateChannel,
    deleteChannel,
    testChannel,
    toggleChannel,
  };
};
